/**
 * SidewalksConfirmation Component
 * Review/confirmation page for sidewalks section (single section with ADA loop)
 */

import React from "react";
import { Trash2 } from "lucide-react";
import LabeledImageUpload from "./LabeledImageUpload";

const SidewalksConfirmation = ({
  currentData,
  onEdit,
  onConfirm,
  onBack,
  canGoBack = false,
  isCompleted = false,
  sessionData,
  onItemClick,
  onAddAnotherItem,
  onDataUpdate,
  projectId,
  clientId,
  onDelete,
  onDeleteItem,
}) => {
  const renderYesNoAnswer = (label, value, editKey) => {
    if (value === undefined || value === null) return null;

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: "var(--text-primary)",
              }}
            >
              {label}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderDataSection = (title, data, editKey) => {
    // Filter out null/undefined values from the data object
    const filteredData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        filteredData[key] = value;
      }
    });

    if (Object.keys(filteredData).length === 0) return null;

    return (
      <div
        style={{
          marginBottom: "25px",
          padding: "15px",
          backgroundColor: "var(--surface-tertiary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h4>
          <button
            onClick={() => {
              console.log(
                "[SidewalksConfirmation - renderDataSection] Edit button clicked!",
              );
              console.log("[SidewalksConfirmation] title:", title);
              console.log("[SidewalksConfirmation] editKey:", editKey);
              console.log("[SidewalksConfirmation] onEdit function:", onEdit);
              console.log(
                "[SidewalksConfirmation] typeof onEdit:",
                typeof onEdit,
              );
              onEdit(editKey);
            }}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          {Object.entries(filteredData).map(([key, value]) => {
            // Format the key name
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());

            // Handle different value types
            let displayValue = value;
            let isImageField = false;

            if (Array.isArray(value)) {
              isImageField = key.includes("image");
              if (!isImageField) {
                displayValue = `${value.length} item(s)`;
              }
            } else if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            } else if (value === "") {
              displayValue = "—";
            }

            // Render editable image upload component
            if (isImageField) {
              return (
                <div key={key} style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {formattedKey}:
                  </span>
                  <LabeledImageUpload
                    images={value || []}
                    onImagesChange={(updatedImages) => {
                      if (onDataUpdate) {
                        onDataUpdate(key, updatedImages);
                      }
                    }}
                    projectId={projectId}
                    clientId={clientId}
                    hideUploadBox={true}
                  />
                </div>
              );
            }

            return (
              <div key={key} style={{ display: "flex", gap: "10px" }}>
                <span style={{ fontWeight: "500", minWidth: "200px" }}>
                  {formattedKey}:
                </span>
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const adaRamps = sessionData?.adaRamps || [];

  // Debug logging
  console.log("SidewalksConfirmation - currentData:", currentData);
  console.log("SidewalksConfirmation - sessionData:", sessionData);
  console.log("SidewalksConfirmation - adaRamps:", adaRamps);

  return (
    <div
      className="sidewalks-confirmation"
      style={{ padding: "20px", position: "relative" }}
    >
      {/* Delete button - top right corner */}
      {onDelete && !isCompleted && (
        <button
          onClick={onDelete}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            padding: "var(--space-2)",
            border: "1px solid var(--gray-600)",
            backgroundColor: "var(--dark-surface)",
            color: "var(--gray-400)",
            cursor: "pointer",
            borderRadius: "var(--radius-md)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all var(--transition-fast)",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#dc2626";
            e.target.style.borderColor = "#dc2626";
            e.target.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "var(--dark-surface)";
            e.target.style.borderColor = "var(--gray-600)";
            e.target.style.color = "var(--gray-400)";
          }}
          title="Clear this section"
        >
          <Trash2 size={16} />
        </button>
      )}
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "20px",
          borderRadius: "4px",
        }}
      >
        <h3
          style={{
            margin: "0 0 25px 0",
            fontSize: "20px",
            paddingBottom: "15px",
            borderBottom: "2px solid var(--border-secondary)",
          }}
        >
          Sidewalks
        </h3>

        {/* Yes/No Questions */}
        <div style={{ marginBottom: "25px" }}>
          {renderYesNoAnswer(
            "Are there sidewalks?",
            currentData.has_sidewalks,
            "has_sidewalks",
          )}
          {renderYesNoAnswer(
            "Is there a sidewalk detail?",
            currentData.has_sidewalk_detail,
            "has_sidewalk_detail",
          )}
          {renderYesNoAnswer(
            "Are there sidewalk spot elevations?",
            currentData.has_spot_elevations,
            "has_spot_elevations",
          )}
          {renderYesNoAnswer(
            "Are there ADA ramps?",
            currentData.has_ada_ramps,
            "has_ada_ramps",
          )}
        </div>

        {/* Sidewalk Detail Data */}
        {currentData.has_sidewalk_detail &&
          renderDataSection(
            "Sidewalk Detail Information",
            {
              sidewalk_detail_images: currentData.sidewalk_detail_images,
              sidewalk_slope_percentage: currentData.sidewalk_slope_percentage,
            },
            "sidewalk_detail_form",
          )}

        {/* Spot Elevation Details */}
        {currentData.has_spot_elevations &&
          renderDataSection(
            "Spot Elevation Details",
            {
              enough_spots_no_offset: currentData.enough_spots_no_offset,
              need_check_max_slope: currentData.need_check_max_slope,
              combine_offset_and_spots: currentData.combine_offset_and_spots,
            },
            "has_spot_elevations",
          )}

        {/* ADA Ramps List */}
        {adaRamps.length > 0 && (
          <div style={{ marginBottom: "25px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "15px",
                gap: "var(--space-3)",
              }}
            >
              <h4 style={{ margin: 0, fontSize: "16px" }}>
                ADA Ramps ({adaRamps.length})
              </h4>

              {/* Add Another ADA Ramp Button */}
              {onAddAnotherItem && (
                <button
                  type="button"
                  onClick={() => onAddAnotherItem("ada-ramps")}
                  style={{
                    padding: "var(--space-2) var(--space-4)",
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-semibold)",
                    border: "none",
                    backgroundColor: "var(--cta-orange)",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "var(--radius-md)",
                    transition: "all var(--transition-normal)",
                    boxShadow: "var(--shadow-sm)",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "var(--cta-orange-hover)";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "var(--shadow-md)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "var(--cta-orange)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "var(--shadow-sm)";
                  }}
                >
                  + New ADA Ramp
                </button>
              )}
            </div>
            {adaRamps.map((ramp, idx) => (
              <div
                key={ramp.id}
                style={{
                  marginBottom: "15px",
                  padding: "15px",
                  backgroundColor:
                    ramp.is_used_on_job === false
                      ? "var(--surface-tertiary)"
                      : "var(--dark-elevated)",
                  border:
                    ramp.is_used_on_job === false
                      ? "1px solid var(--border-secondary)"
                      : "1px solid #4CAF50",
                  borderLeft:
                    ramp.is_used_on_job === false
                      ? "4px solid var(--text-secondary)"
                      : "4px solid #4CAF50",
                  borderRadius: "4px",
                  opacity: ramp.is_used_on_job === false ? 0.7 : 1,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flex: 1,
                    }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: "bold",
                      }}
                    >
                      {ramp.ramp_type || `ADA Ramp ${idx + 1}`}
                    </h5>
                    <span
                      style={{
                        padding: "4px 12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        borderRadius: "12px",
                        backgroundColor:
                          ramp.is_used_on_job === false
                            ? "var(--surface-hover)"
                            : "#4CAF50",
                        color:
                          ramp.is_used_on_job === false
                            ? "#666"
                            : "var(--surface-secondary)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>{ramp.is_used_on_job === false ? "○" : "✓"}</span>
                      {ramp.is_used_on_job === false ? "Not Used" : "In Use"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() =>
                        onItemClick && onItemClick("ada-ramps", ramp, idx)
                      }
                      style={{
                        padding: "6px 20px",
                        fontSize: "14px",
                        border: "1px solid var(--border-primary)",
                        backgroundColor: "var(--surface-secondary)",
                        color: "var(--text-primary)",
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                    {onDeleteItem && (
                      <button
                        onClick={() =>
                          onDeleteItem(
                            "ada-ramps",
                            ramp.id,
                            ramp.ramp_type || `ADA Ramp ${idx + 1}`,
                          )
                        }
                        style={{
                          padding: "var(--space-2)",
                          border: "1px solid var(--gray-600)",
                          backgroundColor: "var(--dark-surface)",
                          color: "var(--gray-400)",
                          cursor: "pointer",
                          borderRadius: "var(--radius-md)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all var(--transition-fast)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#dc2626";
                          e.target.style.borderColor = "#dc2626";
                          e.target.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor =
                            "var(--dark-surface)";
                          e.target.style.borderColor = "var(--gray-600)";
                          e.target.style.color = "var(--gray-400)";
                        }}
                        title="Delete this ADA ramp"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: "grid", gap: "10px" }}>
                  {ramp.ramp_detail_images &&
                    Array.isArray(ramp.ramp_detail_images) &&
                    ramp.ramp_detail_images.length > 0 && (
                      <div style={{ marginBottom: "10px" }}>
                        <span
                          style={{
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-primary)",
                          }}
                        >
                          Detail Images:
                        </span>
                        <LabeledImageUpload
                          images={ramp.ramp_detail_images || []}
                          onImagesChange={(updatedImages) => {
                            // Update this specific ramp's images
                            if (onDataUpdate && sessionData?.adaRamps) {
                              const updatedRamps = [...sessionData.adaRamps];
                              updatedRamps[idx] = {
                                ...updatedRamps[idx],
                                ramp_detail_images: updatedImages,
                              };
                              onDataUpdate("adaRamps", updatedRamps);
                            }
                          }}
                          projectId={projectId}
                          clientId={clientId}
                          hideUploadBox={true}
                        />
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        )}

        {/* Only show Confirm & Continue button if not yet completed */}
        {!isCompleted && (
          <button
            onClick={onConfirm}
            style={{
              padding: "var(--space-3) var(--space-8)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-semibold)",
              border: "1px solid var(--cta-orange)",
              backgroundColor: "var(--cta-orange)",
              color: "white",
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              transition: "all var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }}
          >
            Confirm & Continue
          </button>
        )}
      </div>
    </div>
  );
};

export default SidewalksConfirmation;
